import jsPDF from 'jspdf';
import QRCode from 'qrcode';
import { DEFAULT_DIPLOMA_LAYOUT, normalizeDiplomaLayout, type DiplomaLayout } from '@/hooks/useCertificateConfig';

export interface DiplomaPDFInput {
  participantName: string;
  diplomaName: string;
  certificateId: string;
  issuedAt: string; // ISO
  backgroundImageUrl?: string | null;
  publicUrl: string;
  entities?: string | null;
  layout?: DiplomaLayout | null;
}

const PUBLIC_BASE_URL = (typeof window !== 'undefined' ? window.location.origin : 'https://lideria.org');

/** Colapsa saltos de línea / espacios múltiples para que el texto no se
 *  renderice desplazado o superpuesto sobre el diploma. */
export function sanitizeDiplomaText(text: string): string {
  return (text ?? '').replace(/\s+/g, ' ').trim();
}

export function buildPublicCertUrl(certLogId: string): string {
  return `${PUBLIC_BASE_URL}/c/${certLogId}`;
}

// URL pública para compartir en redes sociales. Apunta a lideria.link, que
// Cloudflare routea a la edge function `news-share` (que también sirve
// certificados en el path /c/{id}) con OG tags correctos.
export function buildSocialShareUrl(certLogId: string): string {
  return `https://lideria.link/${certLogId}`;
}


async function urlToDataUrl(url: string): Promise<string | null> {
  try {
    const res = await fetch(url, { mode: 'cors' });
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

function hexToRgb(hex: string): [number, number, number] {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!m) return [30, 30, 30];
  return [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)];
}

// Carga una imagen con CORS habilitado para poder dibujarla en un canvas sin
// que Chrome contamine el canvas (que impide toBlob()).
async function loadImageCORS(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
    img.src = src;
  });
}

// Renderiza el diploma como Blob PNG usando Canvas 2D. Espeja la misma lógica
// que `generateDiplomaPDF` pero produce una imagen (no un PDF) — pensado para
// subir a Storage y usar como og:image en el share de LinkedIn.
export async function generateDiplomaImage(input: DiplomaPDFInput): Promise<Blob> {
  // A4 landscape (297x210mm) escalado 4x para que la imagen tenga buena
  // resolución en LinkedIn (1188x840 px).
  const SCALE = 4;
  const W = 297 * SCALE; // 1188
  const H = 210 * SCALE; // 840

  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D no soportado');

  const layout = normalizeDiplomaLayout(input.layout ?? DEFAULT_DIPLOMA_LAYOUT);

  // Background
  if (input.backgroundImageUrl) {
    try {
      const img = await loadImageCORS(input.backgroundImageUrl);
      ctx.drawImage(img, 0, 0, W, H);
    } catch {
      // Si falla el load, dibujamos el fondo por defecto abajo.
    }
  }
  if (!ctx.getImageData(0, 0, 1, 1).data[3]) {
    // Canvas vacío → fondo default
    ctx.fillStyle = '#0f172a';
    ctx.fillRect(0, 0, W, H);
    ctx.strokeStyle = '#6366f1';
    ctx.lineWidth = 3 * SCALE;
    ctx.strokeRect(10 * SCALE, 10 * SCALE, W - 20 * SCALE, H - 20 * SCALE);
  }

  const date = new Date(input.issuedAt).toLocaleDateString('es-ES', {
    day: 'numeric', month: 'long', year: 'numeric',
  });
  const shortId = input.certificateId.slice(0, 8).toUpperCase();

  const drawText = (
    el: { enabled: boolean; x: number; y: number; fontSize: number; color: string; align: 'left' | 'center' | 'right' },
    text: string,
    bold = false,
  ) => {
    if (!el.enabled || !text) return;
    text = sanitizeDiplomaText(text);
    if (!text) return;
    // fontSize viene en puntos (mismo que jsPDF). Conversión a canvas px:
    //   1pt = 1/72 inch = 25.4/72 mm ≈ 0.3528 mm
    //   Canvas a SCALE px/mm → 1pt = SCALE * 25.4/72 px
    //   Para SCALE=4 → 1pt = 1.411 px (ej. fontSize 32pt → 45.2px)
    const px = el.fontSize * SCALE * (25.4 / 72);
    ctx.font = `${bold ? 'bold ' : ''}${px}px Helvetica, Arial, sans-serif`;
    ctx.fillStyle = el.color;
    ctx.textAlign = el.align;
    // 'alphabetic' matches jsPDF's default: la línea base del texto se coloca
    // en la coordenada y (el texto queda POR ENCIMA de la línea de subrayado
    // del template, no atravesándola como pasaría con 'middle').
    ctx.textBaseline = 'alphabetic';
    ctx.fillText(text, (el.x / 100) * W, (el.y / 100) * H);
  };

  drawText(layout.participantName, input.participantName, true);
  drawText(layout.diplomaName, input.diplomaName);
  drawText(layout.date, `Fecha de emisión: ${date}`);
  drawText(layout.certificateId, `ID: ${shortId}`);
  if (input.entities) drawText(layout.entities, `Emitido por: ${input.entities}`);
  drawText(layout.publicUrl, `Verificar: ${input.publicUrl}`);

  // QR
  if (layout.qr.enabled) {
    try {
      const qrDataUrl = await QRCode.toDataURL(input.publicUrl, {
        margin: 1,
        width: 256,
        color: { dark: '#000000', light: '#FFFFFF' },
      });
      const qrImg = await loadImageCORS(qrDataUrl);
      const size = layout.qr.size * SCALE;
      const qx = (layout.qr.x / 100) * W - size / 2;
      const qy = (layout.qr.y / 100) * H - size / 2;
      ctx.drawImage(qrImg, qx, qy, size, size);
    } catch {
      // ignore
    }
  }

  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('canvas.toBlob returned null'))),
      'image/png',
      0.95,
    );
  });
}

export async function generateDiplomaPDF(input: DiplomaPDFInput): Promise<void> {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const W = 297;
  const H = 210;

  const layout = normalizeDiplomaLayout(input.layout ?? DEFAULT_DIPLOMA_LAYOUT);

  // Background
  if (input.backgroundImageUrl) {
    const dataUrl = await urlToDataUrl(input.backgroundImageUrl);
    if (dataUrl) {
      try { doc.addImage(dataUrl, 'PNG', 0, 0, W, H); } catch { /* ignore */ }
    }
  }

  if (!input.backgroundImageUrl) {
    doc.setFillColor(15, 23, 42);
    doc.rect(0, 0, W, H, 'F');
    doc.setDrawColor(99, 102, 241);
    doc.setLineWidth(3);
    doc.rect(10, 10, W - 20, H - 20, 'S');
  }

  const date = new Date(input.issuedAt).toLocaleDateString('es-ES', {
    day: 'numeric', month: 'long', year: 'numeric',
  });
  const shortId = input.certificateId.slice(0, 8).toUpperCase();

  const drawText = (
    el: { enabled: boolean; x: number; y: number; fontSize: number; color: string; align: 'left' | 'center' | 'right' },
    text: string,
    bold = false,
  ) => {
    if (!el.enabled || !text) return;
    text = sanitizeDiplomaText(text);
    if (!text) return;
    const [r, g, b] = hexToRgb(el.color);
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    doc.setFontSize(el.fontSize);
    doc.setTextColor(r, g, b);
    doc.text(text, (el.x / 100) * W, (el.y / 100) * H, { align: el.align });
  };

  drawText(layout.participantName, input.participantName, true);
  drawText(layout.diplomaName, input.diplomaName, false);
  drawText(layout.date, `Fecha de emisión: ${date}`);
  drawText(layout.certificateId, `ID: ${shortId}`);
  if (input.entities) drawText(layout.entities, `Emitido por: ${input.entities}`);
  drawText(layout.publicUrl, `Verificar: ${input.publicUrl}`);

  // QR
  if (layout.qr.enabled) {
    try {
      const qrDataUrl = await QRCode.toDataURL(input.publicUrl, {
        margin: 1,
        width: 256,
        color: { dark: '#000000', light: '#FFFFFF' },
      });
      const size = layout.qr.size;
      const qx = (layout.qr.x / 100) * W - size / 2;
      const qy = (layout.qr.y / 100) * H - size / 2;
      doc.addImage(qrDataUrl, 'PNG', qx, qy, size, size);
    } catch { /* ignore */ }
  }

  doc.save(`diploma-${sanitizeDiplomaText(input.participantName).replace(/\s+/g, '-')}-${shortId}.pdf`);
}
